import { useMemo, useState } from 'react'
import { createFileRoute, useBlocker } from '@tanstack/react-router'
import { DEFAULT_ROUTING, ORDER_BUDGET_USD, PRICES, type PipelineStep, type RoutingConfig } from '@hedgehog/shared'
import { api } from '@/lib/api'
import { opsApi, useOpsMutation } from '@/components/ops/api'
import { parseRouting } from '@/components/ops/routing-schema'
import { formatUsd } from '@/components/ops/format'
import { Section } from '@/components/ops/section'
import { ErrorState, FormError, LoadingState } from '@/components/app/states'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/ops/settings')({
  loader: () => opsApi<RoutingConfig>('/ops/settings/routing'),
  head: () => ({ meta: [{ title: 'Settings · Ops' }] }),
  component: SettingsPage,
  pendingComponent: () => <LoadingState label="Loading settings" />,
  errorComponent: ({ error }) => <ErrorState error={error} title="Could not load the routing settings" />,
})

const pretty = (v: unknown) => JSON.stringify(v, null, 2)

function modelLabel(m: { provider: string; model: string; batch?: boolean }) {
  return `${m.model}${m.batch ? ' (batch)' : ''}`
}

function SettingsPage() {
  const loaded = Route.useLoaderData()
  const loadedText = useMemo(() => pretty(loaded), [loaded])
  const [text, setText] = useState(loadedText)
  const [saved, setSaved] = useState<string | null>(null)
  const { run, pending, error } = useOpsMutation()

  const parsed = useMemo(() => parseRouting(text), [text])
  const dirty = text !== loadedText
  const unknownModels = parsed.ok
    ? Array.from(
        new Set(
          Object.values(parsed.value.steps)
            .flatMap((s) => [s.primary.model, s.fallback.model])
            .filter((m) => !(m in PRICES)),
        ),
      )
    : []
  const differsFromDefault = parsed.ok && pretty(parsed.value) !== pretty(DEFAULT_ROUTING)

  const blocker = useBlocker({ shouldBlockFn: () => dirty, enableBeforeUnload: () => dirty, withResolver: true })

  async function save() {
    if (!parsed.ok) return
    setSaved(null)
    const res = await run(() => api<RoutingConfig>('/ops/settings/routing', { method: 'PUT', json: parsed.value }))
    if (res) {
      setText(pretty(res))
      setSaved('Saved. New jobs use this routing.')
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h1 className="text-base font-semibold">Model routing</h1>
        <p className="text-muted-foreground">
          Which model runs each pipeline step (MR-4). Changes apply to jobs claimed after saving; running jobs keep their routing.
        </p>
      </div>

      <div className="grid items-start gap-3 xl:grid-cols-[minmax(0,1fr)_34rem]">
        <Section
          id="routing-json"
          title="Routing JSON"
          actions={
            <span className={cn('text-xs', dirty ? 'text-warning' : 'text-muted-foreground')} aria-live="polite">
              {dirty ? 'Unsaved changes' : 'Saved version'}
            </span>
          }
        >
          <div className="flex flex-col gap-2">
            <Label htmlFor="routing-text" className="sr-only">
              Routing configuration JSON
            </Label>
            <textarea
              id="routing-text"
              value={text}
              onChange={(e) => {
                setText(e.target.value)
                setSaved(null)
              }}
              spellCheck={false}
              aria-invalid={!parsed.ok || undefined}
              aria-describedby="routing-validation"
              className="min-h-[60vh] w-full rounded-lg border border-input bg-transparent p-3 font-mono text-xs leading-relaxed outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 aria-invalid:border-destructive"
            />
            <div id="routing-validation" aria-live="polite">
              {parsed.ok ? (
                <p className="text-xs text-success">Valid routing configuration.</p>
              ) : (
                <ul className="flex flex-col gap-0.5 text-xs text-destructive">
                  {parsed.errors.slice(0, 12).map((e) => (
                    <li key={e} className="font-mono">
                      {e}
                    </li>
                  ))}
                  {parsed.errors.length > 12 && <li>…and {parsed.errors.length - 12} more</li>}
                </ul>
              )}
              {unknownModels.length > 0 && (
                <p className="mt-1 text-xs text-warning">
                  No price on file for {unknownModels.join(', ')}. Cost accounting will record $0 for these runs until PRICES in packages/shared is
                  updated.
                </p>
              )}
            </div>
            <FormError message={error} />
            {saved && (
              <p role="status" className="text-xs text-success">
                {saved}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              <Button onClick={save} disabled={!parsed.ok || !dirty || pending}>
                {pending ? 'Saving…' : 'Save'}
              </Button>
              <Button variant="outline" onClick={() => setText(loadedText)} disabled={!dirty || pending}>
                Discard changes
              </Button>
              <Button variant="ghost" onClick={() => setText(pretty(DEFAULT_ROUTING))} disabled={pending}>
                Load defaults
              </Button>
              {differsFromDefault && <span className="self-center text-xs text-muted-foreground">Differs from the built-in defaults.</span>}
            </div>
          </div>
        </Section>

        <Section id="routing-preview" title="Effective routing">
          {parsed.ok ? (
            <div className="flex flex-col gap-3">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Step</TableHead>
                    <TableHead>Primary</TableHead>
                    <TableHead>Fallback</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(Object.entries(parsed.value.steps) as Array<[PipelineStep, RoutingConfig['steps'][PipelineStep]]>).map(([step, r]) => (
                    <TableRow key={step}>
                      <TableCell className="py-1 text-xs">
                        {step}
                        {r.offPeak && <span className="ml-1 text-muted-foreground">(off-peak)</span>}
                      </TableCell>
                      <TableCell className="py-1 text-xs whitespace-normal break-all">{modelLabel(r.primary)}</TableCell>
                      <TableCell className="py-1 text-xs whitespace-normal break-all text-muted-foreground">{modelLabel(r.fallback)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <p className="text-xs text-muted-foreground">
                AI budget: {formatUsd(ORDER_BUDGET_USD)} per order, all jobs together. A job stops when an order reaches it; ops can change it on the order.
              </p>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Fix the JSON to see the routing table.</p>
          )}
        </Section>
      </div>

      <Dialog open={blocker.status === 'blocked'} onOpenChange={(o) => !o && blocker.reset?.()}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Leave without saving?</DialogTitle>
            <DialogDescription>Your routing changes are not saved and will be lost.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => blocker.reset?.()}>
              Stay
            </Button>
            <Button variant="destructive" onClick={() => blocker.proceed?.()}>
              Discard and leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
